import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Display } from '@/types/display';
import { normalizePhotos } from '@/utils/displayUtils';
import { fetchDisplayDetail } from '@/services/displayDetailService';

// Helper function to transform the database response to match our Display type
const transformDatabaseResponse = (data: any): Display => {
  // Parse the schedule if it's a string, or pass through if it's already an object
  let scheduleObject;
  if (typeof data.schedule === 'string') {
    try {
      scheduleObject = JSON.parse(data.schedule);
    } catch (e) {
      console.warn('Failed to parse schedule JSON for display:', data.id, e);
      scheduleObject = null;
    }
  } else {
    scheduleObject = data.schedule;
  }
  
  // Handle both enhanced and legacy schedule formats
  let finalSchedule = null;
  
  if (scheduleObject) {
    try {
      if (scheduleObject.timezone && scheduleObject.regular_hours) {
        // Enhanced schedule format - pass through
        finalSchedule = scheduleObject;
      } else if (scheduleObject.start_date && scheduleObject.end_date && scheduleObject.days && scheduleObject.hours) {
        // Legacy schedule format - pass through
        finalSchedule = scheduleObject;
      } else if (scheduleObject.start_date || scheduleObject.hours) {
        // Partial legacy schedule - fill in missing parts
        finalSchedule = {
          start_date: scheduleObject.start_date || '',
          end_date: scheduleObject.end_date || '',
          days: scheduleObject.days || [],
          hours: {
            start: scheduleObject.hours?.start || '',
            end: scheduleObject.hours?.end || ''
          }
        };
      }
    } catch (error) {
      console.warn('Error processing schedule object for display:', data.id, error);
      finalSchedule = null;
    }
  }
  
  return {
    ...data,
    schedule: finalSchedule,
    photos: normalizePhotos(data.photos),
    // "Last Updated" prefers the row timestamp; the legacy `updated` column
    // is only populated on ~half the rows
    updated: data.updated_at || data.updated || null,
    // Ensure the new fields exist (even if null)
    parking_other: data.parking_other || null,
    facebook: data.facebook || null,
    website: data.website || null,
    city: data.city || null,
    state: data.state || null
  };
};

// Column-level grants replaced the table-wide SELECT on `displays`, so
// select('*') is refused for the browser. Name the public columns; the
// precise location lives in exact_* and is not readable here by design.
export const PUBLIC_DISPLAY_COLUMNS = [
  'id', 'slug', 'name', 'description', 'location', 'city', 'state',
  'latitude', 'longitude', 'location_precision', 'image_url', 'photos',
  'display_type', 'holidays', 'holiday_type', 'year_started', 'fm_station',
  'parking_other', 'facebook', 'website', 'instagram', 'schedule', 'tags',
  'light_count', 'projector_used', 'illumibot_used', 'software', 'controllers',
  'controllers_short', 'last_confirmed_at', 'average_rating', 'rating_count',
  'rating_distribution', 'upvote_count', 'checkin_count', 'views_count',
  'featured', 'status', 'user_id', 'updated', 'updated_at', 'created_at', 'age_rating',
].join(', ');

export const useDisplays = () => {
  return useQuery({
    queryKey: ['displays'],
    queryFn: async (): Promise<Display[]> => {
      // Query to get active displays with images and valid coordinates
      const { data, error } = await supabase
        .from('displays')
        .select(PUBLIC_DISPLAY_COLUMNS)
        .eq('status', 'active')
        .not('image_url', 'is', null)
        .neq('image_url', '')
        .not('latitude', 'is', null)
        .not('longitude', 'is', null)
        .neq('latitude', 0)
        .neq('longitude', 0);
      
      if (error) {
        console.error('Error fetching displays:', error);
        throw new Error(error.message);
      }
      
      // Transform each database record to match our Display type
      return (data || []).map(transformDatabaseResponse);
    }
  });
};

export const useDisplay = (idOrSlug: number | string | undefined) => {
  // Determine if the parameter is a numeric ID or a slug
  const isNumericId = typeof idOrSlug === 'number' || (typeof idOrSlug === 'string' && !isNaN(Number(idOrSlug)));
  const id = isNumericId && typeof idOrSlug !== 'undefined' ? Number(idOrSlug) : undefined;
  const slug = !isNumericId && typeof idOrSlug === 'string' ? idOrSlug : undefined;

  return useQuery({
    queryKey: ['display', idOrSlug],
    queryFn: async (): Promise<Display | null> => {
      if (!id && !slug) return null;
      const data = await fetchDisplayDetail(id ?? slug!);
      if (!data) return null;

      const transformedData = transformDatabaseResponse(data);
      if (data.user_profile) {
        transformedData.owner = {
          id: data.user_profile.user_id,
          name: data.user_profile.display_name || data.user_profile.full_name || 'Unknown Owner',
          avatar: data.user_profile.avatar_url,
          bio: data.user_profile.bio ?? undefined,
          // social_links is Json in the generated types
          socialLinks: (data.user_profile.social_links ?? undefined) as NonNullable<Display['owner']>['socialLinks']
        };
      }

      return transformedData;
    },
    enabled: !!(id || slug)
  });
};

export const useFeaturedDisplays = () => {
  return useQuery({
    queryKey: ['featured-displays'],
    queryFn: async (): Promise<Display[]> => {
      // Query to get featured displays with images
      const { data, error } = await supabase
        .from('displays')
        .select(PUBLIC_DISPLAY_COLUMNS)
        .eq('status', 'active')
        .eq('featured', true)
        .not('image_url', 'is', null)
        .neq('image_url', '');
      
      if (error) {
        console.error('Error fetching featured displays:', error);
        throw new Error(error.message);
      }
      
      // Transform each database record to match our Display type
      return (data || []).map(transformDatabaseResponse);
    }
  });
};
